import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  ViewChild,
  computed,
  effect,
  input,
  output,
  signal,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { Utilities } from '@xmedical/core';

const DEFAULT_AVATAR = '/assets/img/default-avatar.png';

// Pure I/O patient profile picture display + file picker. The container is
// responsible for resolving the display URL from the patient's stored key
// (signed S3 GET) and for uploading the new file (signed S3 PUT). This
// component:
//
// 1. Renders [imageUrl] as the displayed image, or a default avatar when
//    empty/null.
// 2. On a local file pick, generates a new key (preserving the existing
//    `uploads/patients/images/{uuid}.{ext}` convention), starts a local
//    FileReader preview so the new image appears instantly, and emits
//    (fileSelected) so the container can patch the form control + start
//    the upload.
@Component({
  selector: 'xcs-patient-profile-picture',
  templateUrl: './patient-profile-picture.component.html',
  styleUrls: ['./patient-profile-picture.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatIconModule],
})
export class PatientProfilePictureComponent {

  // The container resolves a signed GET URL for the patient's stored key and
  // passes it in. Empty string falls back to the default avatar.
  readonly imageUrl = input<string>('');

  // Emitted once when the user picks a file. Container patches the form's
  // `profilePictureUrl` control with `newKey` and runs the upload.
  readonly fileSelected = output<{ file: File; newKey: string }>();

  // Currently displayed image. Starts as imageUrl(), switches to a FileReader
  // data URL after the user picks a file (until imageUrl() is updated by the
  // container with a freshly resolved signed URL for the new key).
  private readonly localPreview = signal<string | null>(null);
  readonly imgSrc = computed(() => this.localPreview() ?? (this.imageUrl() || DEFAULT_AVATAR));

  // Drop the local preview when the container re-resolves imageUrl() — this
  // typically happens after the upload completes and the form re-loads from
  // the patient store with the new signed URL.
  private readonly resetPreviewOnUrlChange = effect(() => {
    this.imageUrl();
    this.localPreview.set(null);
  });

  @ViewChild('fileInput') private fileInput!: ElementRef<HTMLInputElement>;

  openFileBrowser(): void {
    this.fileInput.nativeElement.click();
  }

  onFileChanged(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) {
      return;
    }
    const ext = file.name.split('.').pop() ?? '';
    const name = Utilities.generateUuid() + (ext ? '.' + ext : '');
    const newKey = 'uploads/patients/images/' + encodeURI(name);
    this.previewLocally(file);
    this.fileSelected.emit({ file, newKey });
  }

  private previewLocally(file: File): void {
    const reader = new FileReader();
    reader.onloadend = (e) => {
      const target = e.target as FileReader;
      if (typeof target.result === 'string') {
        this.localPreview.set(target.result);
      }
    };
    reader.readAsDataURL(file);
  }
}
